import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Drawer, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { PaletteIcon, XIcon } from 'lucide-react'
import { Button } from '@repo/ui'

import { ROUTES } from 'routes'
import { useCurrentUserCompany } from 'hooks/api/useCompanyQuery'
import { useGetCompanyStyles } from 'hooks/api/useGetCompanyStyles'
import { Preview } from 'pages/main/StudioPage/components/Preview'
import { vhMinus } from 'utils/textZoom'

/**
 * See a certificate as the public sees it, WITHOUT activating it.
 *
 * ## Why a drawer and not a page
 *
 * This replaced a `/certificates/:id/preview` route. Studio already renders the
 * certificate preview in a right-hand pane, so a second preview on its own route, at a
 * different size, was a second surface for one job — the drift pattern this codebase keeps
 * losing to (§36). Same `Preview` component, two containers: Studio's split pane and this
 * drawer.
 *
 * Removing the page also removed a real bug rather than needing it fixed: that route's
 * breadcrumb sat 4px higher and its title 30px lower than the certificate page's, because
 * the header offset was inherited from whatever wrapped it. A drawer has no page header,
 * so there is nothing to misalign.
 *
 * ## Why activation is irrelevant here
 *
 * Activation writes a metering row and debits the ledger — it is billable. The public read
 * path applies no activation filter (deliberately: a shelf certificate must stay scannable
 * for an in-store tap), so the preview works on any certificate, activated or not, without
 * costing anything. That is the whole point of this surface.
 *
 * ## Width
 *
 * Sized to the 375x812 phone frame at the preview's default zoom plus device chrome and
 * padding — NOT to Studio's proportion, which is generous because that page is a two-pane
 * split by design. The certificate detail page behind this is dense, so the drawer takes
 * only what the phone actually needs.
 */

/**
 * 500, matching the Filters drawer exactly rather than being sized to the phone frame.
 *
 * The first version picked its own width from the content (520) and sat flush against the
 * viewport edge. That made it a second kind of drawer in an app that already has one —
 * and the house drawer is INSET: `margin: 4` with `borderRadius: 3`, so it floats over the
 * page rather than welding itself to the edge. Matching Filters means a user who has
 * opened one has already learned this one (§28).
 */
const DRAWER_WIDTH = 500

interface CertificatePreviewDrawerProps {
  open: boolean
  onClose: () => void
  certificateId: string
  /** Shown in the drawer header so it is obvious which record is being previewed. */
  title?: string
}

export const CertificatePreviewDrawer = ({
  open,
  onClose,
  certificateId,
  title,
}: CertificatePreviewDrawerProps) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const { data: company } = useCurrentUserCompany()
  const { data: companyStylesResponse } = useGetCompanyStyles({ companyId: company?.id })

  // The SAVED theme, not Studio's dirty form state — this previews the certificate as it
  // stands today, not an in-progress restyle. Studio remains where you try changes.
  const savedTheme = useMemo(() => companyStylesResponse?.styles ?? {}, [companyStylesResponse])

  const goToStudio = () => {
    // Studio is a CROSS-SURFACE jump (not an ancestor of a certificate), so passing the
    // referrer is what makes the shared Header render "< Back to …" over there — §42. It
    // is also the answer to "Style in Studio needs a back button": the back affordance is
    // the referrer link, not a bespoke button.
    navigate(ROUTES.STUDIO, {
      state: { backTo: `${location.pathname}${location.search}`, backLabel: title || 'Certificate' },
    })
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          // The house drawer floats: inset by 16px with a 12px radius, rather than welded
          // to the viewport edge. Copied from Filters so the two read as one component.
          margin: 4,
          borderRadius: 3,
          maxHeight: vhMinus(32),
          minWidth: DRAWER_WIDTH,
          maxWidth: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          // Kill MUI's dark-mode elevation overlay so header, body and footer read as one
          // uniform surface instead of three subtly different shades (same fix as Filters).
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      {/*
        Actions sit at the TOP, not in a footer.
        A footer is where a task is COMMITTED — Filters puts Clear/Apply there because
        those finish the job the drawer exists for. This drawer commits nothing; it is a
        viewing surface, and "Style in Studio" is navigation OUT of it. One lone button in
        a footer also reads as an unfinished form.
        The original crowding came from the DESCRIPTION sharing the row, not from the
        button — it was wrapping under it at drawer width. So the description moves to its
        own line beneath (§40) and the top row carries title + action + close, which fits
        comfortably in 500px.
      */}
      <Stack sx={{ p: 6, pb: 4, flexShrink: 0 }} spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
          <Typography variant="body1" sx={{ flexShrink: 0 }}>Preview</Typography>

          <Stack direction="row" alignItems="center" spacing={3} sx={{ flexShrink: 0 }}>
            <Button
              variant="secondary"
              startIcon={<PaletteIcon strokeWidth={1} />}
              onClick={goToStudio}
            >
              Style in Studio
            </Button>
            {/* X, not a trash glyph — this dismisses an overlay, it destroys nothing (§38). */}
            <IconButton
              onClick={onClose}
              aria-label="Close preview"
              sx={{ color: theme.palette.text.primary }}
            >
              <XIcon strokeWidth={1} />
            </IconButton>
          </Stack>
        </Stack>

        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          How the certificate appears to anyone who opens it.
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Preview
          formValues={savedTheme}
          previewPage="certificate"
          device="mobile"
          certificateId={certificateId}
          // 1, not Studio's 1.35. Studio previews inside a wide two-pane split; this drawer
          // is 500px and the phone came out bigger than the space holding it.
          baseZoom={1}
          // The pane's height belongs to the container too, and this container is NOT the
          // page. The drawer body ends 16px above the viewport bottom while the pane starts
          // 60px down inside it, so the pane has `vh - 201` to work with — anything taller is
          // silently clipped by the body's `overflow: hidden`. Measured at 1280x800: wrapper
          // 659px, pane 551px, 48px of slack. Studio's default is `vh - 185` (615px), which
          // would overrun by 16px, so this pins the geometry rather than inheriting it.
          heightOffset={249}
          // 60, not Studio's 137.5: that number clears Studio's pinned header block, which
          // this drawer does not have. Its own body is the bound, and the pane's 60px inset
          // inside it is the measured resting geometry above.
          stickyTop={60}
          // Flush: the drawer has no tab row above the pane, so Studio's 16px of
          // air would just be dead space at the top of a 500px panel.
          topGap={0}
        />
      </Box>
    </Drawer>
  )
}
